import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { JwtService } from 'src/auth/services/jwt.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class ScreenShareService {
  private hubConnection: signalR.HubConnection | null = null;
  private connectPromise: Promise<void> | null = null;

  private frameSocket: WebSocket | null = null;

  /** Emits a Blob for each received frame, or null when the session ends. */
  readonly frame$ = new Subject<Blob | null>();
  /** Emits the sharer's email when a session starts, null when it ends. */
  readonly session$ = new Subject<string | null>();

  constructor(
    private readonly jwtService: JwtService,
    private readonly toastService: ToastService,
  ) {}

  connect(): Promise<void> {
    if (this.connectPromise) return this.connectPromise;

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl('/hubs/screenshare', {
        accessTokenFactory: () => this.jwtService.getToken() ?? '',
      })
      .withAutomaticReconnect()
      .build();

    this.connectPromise = this.hubConnection
      .start()
      .then(() => {
        return this.hubConnection!.invoke('Register');
      })
      .catch((err) => {
        console.error('[ScreenShare:Cert] Hub connection failed:', err);
        this.hubConnection = null;
        this.connectPromise = null;
      });

    this.registerHubEventHandlers();

    return this.connectPromise;
  }

  registerHubEventHandlers(): void {
    this.hubConnection?.on('ScreenShareDeclined', (sharerEmail: string) => {
      this.toastService.error(`Your screen share request to ${sharerEmail} was declined.`);
    });

    this.hubConnection?.on('ScreenShareAccepted', (sharerEmail: string) => {
      this.toastService.success(`Screen share session with ${sharerEmail} accepted. Connecting...`);
      this.openFrameReceiver(sharerEmail);
    });

    this.hubConnection?.on('ScreenShareStopped', () => {
      this.stopViewing();
    });

    this.hubConnection?.on('ScreenShareEnded', () => {
      this.stopViewing();
    });
  }

  // Sends a request to the hub
  async requestScreenShare(targetUserEmail: string): Promise<void> {
    await this.connect();
    await this.hubConnection?.invoke('RequestScreenShare', targetUserEmail);
  }

  async stopScreenShare(sharerEmail: string): Promise<void> {
    await this.hubConnection?.invoke('StopScreenShare', sharerEmail);
    this.stopViewing();
  }

  openFrameReceiver(sharerEmail: string): void {
    // Release any existing session (does not close CONNECTING sockets — see stopViewing)
    this.stopViewing();

    const wsProtocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${location.host}/ws/frames?access_token=${encodeURIComponent(this.jwtService.getToken() ?? '')}&role=receiver`;
    const socket = new WebSocket(wsUrl);
    this.frameSocket = socket;
    socket.binaryType = 'blob';

    // Use the local 'socket' reference so stale close/open events from a prior
    // socket never operate on whichever socket is currently assigned to this.frameSocket.

    let sessionEstablished = false;

    socket.addEventListener('open', () => {
      if (this.frameSocket !== socket) {
        socket.close();
        return;
      }
      sessionEstablished = true;
      this.session$.next(sharerEmail);
    });

    socket.addEventListener('message', (event) => {
      this.frame$.next(event.data);
    });

    socket.addEventListener('close', () => {
      if (this.frameSocket !== socket) {
        // stopViewing() was already called externally (e.g. ScreenShareStopped hub event);
        // the socket was orphaned — no further action needed.
        return;
      }
      if (sessionEstablished) {
        // The session was live and has now ended (sender closed or network drop).
        this.stopViewing();
      } else {
        // The WebSocket never opened — the connection itself failed.
        // Avoid showing "session ended" when a session never started.
        this.frameSocket = null;
        this.session$.next(null);
        this.toastService.error('Could not connect to the screen share session. Ask the educator to try again.');
      }
    });

    socket.addEventListener('error', (err) => {
      console.error('[ScreenShare:Cert] Frame WebSocket error:', err);
    });
  }

  stopViewing() {
    if (this.frameSocket) {
      const socket = this.frameSocket;
      this.frameSocket = null; // release the reference first
      this.session$.next(null);
      this.toastService.info(`Screen share session ended.`);
      if (socket.readyState === WebSocket.OPEN) {
        socket.close();
      }
      // CONNECTING sockets are not explicitly closed here — they are abandoned.
      // When they eventually connect, the 'open' handler detects they are no
      // longer the active socket and closes them there.
    }
  }

  disconnect(): void {
    this.hubConnection?.stop();
    this.hubConnection = null;
    this.connectPromise = null;
  }
}
